import { Box, Grid, makeStyles } from "@material-ui/core";
import { DsExternalTextLink, DsText, DsTooltip, size } from "@mvp/design-system";
import React from "react";
import { useAppStore } from "../../../hooks/useAppStore";
import useGetClassInfo from "../../../hooks/useGetClassInfo";
import { usePlanUrls } from "../../../hooks/usePlanUrls";
import { NA } from "../../../store/constants";
import Icons from "../../../utils/icons";
import { EffectiveDateSummary } from "./EffectiveDateSummary";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  infoIcon: {
    color: "rgba(0, 0, 0, 0.6)",
    alignSelf: "center",
  },
  textMargin: {
    marginRight: size.xs(theme),
  },
  textColor: {
    color: theme.palette.grey[500],
  },
  planType: {
    textTransform: 'capitalize'
  },

  planNameStyles: {
    margin: `0 ${size.xs(theme)}`,
  },
  externalLink: {
    color: "rgba(0, 0, 0, 0.6)",
    padding: 0,
    lineHeight: size.m(theme),
    textDecoration: 'underline',
    display: 'inline-block',
    [theme.breakpoints.down("sm")]: {
      padding: `0 ${size.xs(theme)}`,
    },
  },
  blackWithSixtyAlpha: {
    color: "rgba(0, 0, 0, 0.6)",
  },
}));

export const Summary: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { appStore: { planInfo } } = useAppStore();
  const { getPlanRate, rateClassObj } = useGetClassInfo();
  const { planUrl: overviewUrl } = usePlanUrls(planInfo?.planSlug || "", true);
  const rateInfo = rateClassObj();

  return (
    <Grid container spacing={4}>
      <Grid item xs={4}>
        <DsText variant="overline" text={t("planDetails.planName")} />
        <DsText variant="body2" text={planInfo?.planName || NA} />
        <DsExternalTextLink
            openInNewTab
            href={overviewUrl}
            variant="overline"
            className={classes.externalLink}
            text={planInfo?.coPlan || NA}
        />
      </Grid>
      <Grid item xs={4}>
        <DsText variant="overline" text={t("planDetails.effectiveDate")} />
        <EffectiveDateSummary />
      </Grid>
      <Grid item xs={4}>
        <DsText variant="overline" text={t("planDetails.monthlyPremium")} />
        <Box display="flex">
          <DsText variant="body2" className={classes.textMargin}>{`$${getPlanRate()} / ${t('common.month')}`}</DsText>
          <DsTooltip arrow title={<>{rateInfo?.tooltip}</>}>
            <Icons.InfoIcon className={classes.infoIcon} />
          </DsTooltip>
        </Box>
        <DsText variant="caption" className={`${classes.textColor} ${classes.planType}`}>
          {rateInfo?.name?.toUpperCase() === 'SINGLE' ? t("planDetails.singlePlan") : t("planDetails.familyPlan")}
        </DsText>
      </Grid>
    </Grid>
  );
};

export const MobileSummary: React.FC<{ isOpen: boolean }> = (props) => {
  const classes = useStyles();
  const {
    appStore: { planInfo },
  } = useAppStore();
  const { getPlanRate, rateClassObj } = useGetClassInfo();
  const { planUrl: overviewUrl } = usePlanUrls(planInfo?.planSlug || "", true);
  const rateInfo = rateClassObj();

  return (
    <Grid container>
      <Grid item xs={6}>
        <DsText
          variant="subtitle2"
          text={planInfo?.planName || NA}
          className={classes.planNameStyles}
        />
        {props.isOpen ? (
          <DsExternalTextLink
            openInNewTab
            href={overviewUrl}
            variant="overline"
            className={classes.externalLink}
            text={planInfo?.coPlan || NA}
          />
        ) : null}
      </Grid>
      <Grid item xs={6}>
        <DsText variant="overline" className={classes.blackWithSixtyAlpha}>Monthly Premium</DsText>
        <Box display="flex">
          <DsText
            variant="body2"
            className={classes.textMargin}
          >{`$${getPlanRate()} / month`}</DsText>
          <DsTooltip arrow title={<>{rateInfo?.tooltip}</>}>
            <Icons.InfoIcon className={classes.infoIcon} />
          </DsTooltip>
        </Box>
        {props.isOpen ? (
          <>
            <DsText variant="overline" className={classes.blackWithSixtyAlpha}>Effective Date</DsText>
            <Box display="flex">
              <EffectiveDateSummary />
            </Box>
          </>
        ) : null}
      </Grid>
    </Grid>
  );
};